require('./network.sass');
const u = up.util;
up.network = (function () {
    const config = new up.Config(() => ({
        concurrency: 6,
        wrapMethod: true,
        cacheSize: 70,
        cacheExpireAge: 15 * 1000,
        cacheEvictAge: 90 * 60 * 1000,
        lateDelay: 400,
        fail(response) { return (response.status < 200 || response.status > 299) && response.status !== 304; },
        autoCache(request) { return request.isSafe(); },
        expireCache(request) { return !request.isSafe(); },
        evictCache: false,
        progressBar: true,
        timeout: 90_000,
    }));
    const queue = new up.Request.Queue();
    const cache = new up.Request.Cache();
    let progressBar = null;
    function reset() {
        abortRequests();
        queue.reset();
        cache.reset();
        progressBar?.destroy();
        progressBar = null;
    }
    function makeRequest(...args) {
        const options = parseRequestOptions(args);
        const request = new up.Request(options);
        processRequest(request);
        return request;
    }
    function parseRequestOptions(args) {
        let options = u.parseArgIntoOptions(args, 'url');
        up.migrate.handleRequestOptions?.(options);
        return options;
    }
    function processRequest(request) {
        cache.expire(request.expireCache ?? u.evalOption(config.expireCache, request) ?? false);
        cache.evict(request.evictCache ?? u.evalOption(config.evictCache, request) ?? false);
        useCachedRequest(request) || queueRequest(request);
    }
    function useCachedRequest(newRequest) {
        let cachedRequest;
        if (newRequest.willCache() && (cachedRequest = cache.get(newRequest))) {
            up.puts('up.request()', 'Re-using previous request to %s', newRequest.description);
            if (!newRequest.background) {
                queue.promoteToForeground(cachedRequest);
            }
            cachedRequest.mergeIfUnsent(newRequest);
            cache.track(cachedRequest, newRequest, { onIncompatible: processRequest });
            return true;
        }
    }
    function queueRequest(request) {
        handleCaching(request);
        queue.asap(request);
        return true;
    }
    function handleCaching(request) {
        if (request.willCache()) {
            cache.put(request);
            request.onLoading = () => cache.reindex(request);
        }
        u.always(request, function (responseOrError) {
            cache.expire(responseOrError.expireCache ?? false, { except: request });
            cache.evict(responseOrError.evictCache ?? false, { except: request });
            let isResponse = responseOrError instanceof up.Response;
            let isNetworkError = !isResponse;
            let isSuccessResponse = isResponse && responseOrError.ok;
            let isErrorResponse = isResponse && !responseOrError.ok;
            let isEmptyResponse = isResponse && responseOrError.none;
            let redirectRequest = isResponse && responseOrError.redirectRequest;
            if (isErrorResponse) {
                cache.evict(request.url);
            }
            else if (isNetworkError || isEmptyResponse) {
                cache.evict(request);
            }
            else if (isSuccessResponse) {
                if (cache.get(request)) {
                    cache.put(request);
                }
                if (redirectRequest && (redirectRequest.willCache() || cache.get(redirectRequest))) {
                    cache.put(redirectRequest);
                }
            }
        });
    }
    function isBusy() {
        return queue.isBusy();
    }
    function loadPage(requestsAttrs) {
        new up.Request(requestsAttrs).loadPage();
    }
    function abortRequests(...args) {
        up.migrate.preprocessAbortArgs?.(args);
        queue.abort(...args);
    }
    function isSafeMethod(method) {
        return u.contains(['GET', 'OPTIONS', 'HEAD'], u.normalizeMethod(method));
    }
    function onLate() {
        if (u.evalOption(config.progressBar)) {
            progressBar = new up.ProgressBar();
        }
    }
    function onRecover() {
        progressBar?.conclude();
    }
    up.on('up:network:late', onLate);
    up.on('up:network:recover', onRecover);
    up.on('up:framework:reset', reset);
    return {
        request: makeRequest,
        cache,
        isBusy,
        isSafeMethod,
        config,
        abort: abortRequests,
        queue,
        loadPage,
    };
})();
up.request = up.network.request;
up.cache = up.network.cache;
